import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { toast } from "../../../components/AppToast";
import { ORG_TYPES_MASTER, ORG_STATUS, COUNTRIES, TIME_ZONES, CURRENCIES, CONTACT_ROLES } from "./orgConstants";
import { validateReparent, buildTreeWithMeta } from "./orgTreeUtils";
import { emptyContact, emptyPortalAdmin } from "./customerModel";
import { getUsersRolesList } from "../../../services/tenant/tenant";
import { PHONE_COUNTRY_CODES_LIST } from "../../../data/static";

const PHONE_COUNTRY_CODES = PHONE_COUNTRY_CODES_LIST;

const ORG_ROW_DEFAULTS = {
  shortName: "",
  effectiveFrom: "",
  effectiveTo: "",
  legalEntity: false,
  operationalEntity: true,
  country: "IN",
  state: "",
  city: "",
  address1: "",
  address2: "",
  postalCode: "",
  timeZone: "Asia/Kolkata",
  currency: "INR",
  primaryContact: "",
  primaryEmail: "",
  primaryMobile: "",
  contacts: [],
  costCenter: "",
  businessUnit: "",
  departmentType: "",
  mappedUserIds: [],
  mappedCustomerIds: [],
  skuOwnershipNote: "",
  gstin: "",
  pan: "",
  portalRoleId: "",
  portalAdmin: emptyPortalAdmin(),
};

function initialForm() {
  return {
    code: "",
    name: "",
    shortName: "",
    type: "Company",
    parentId: null,
    status: "Active",
    legalEntity: false,
    operationalEntity: true,
    effectiveFrom: "",
    effectiveTo: "",
    currency: "INR",
    country: "IN",
    state: "",
    city: "",
    address1: "",
    address2: "",
    postalCode: "",
    timeZone: "Asia/Kolkata",
    primaryContact: "",
    primaryEmail: "",
    primaryMobile: "",
    contacts: [],
    portalRoleId: "",
    portalAdmin: emptyPortalAdmin(),
  };
}

function normalizeOrgContacts(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((x) => ({
    ...emptyContact(),
    ...x,
    id: x.id || emptyContact().id,
  }));
}

function formFromExisting(org) {
  return {
    ...initialForm(),
    code: org.code ?? "",
    name: org.name ?? "",
    shortName: org.shortName ?? "",
    type: org.type ?? "Company",
    parentId: org.parentId ?? null,
    status: org.status ?? "Active",
    legalEntity: !!org.legalEntity,
    operationalEntity: org.operationalEntity !== false,
    effectiveFrom: org.effectiveFrom ?? "",
    effectiveTo: org.effectiveTo ?? "",
    currency: org.currency ?? "INR",
    country: org.country ?? "IN",
    state: org.state ?? "",
    city: org.city ?? "",
    address1: org.address1 ?? "",
    address2: org.address2 ?? "",
    postalCode: org.postalCode ?? "",
    timeZone: org.timeZone ?? "Asia/Kolkata",
    primaryContact: org.primaryContact ?? "",
    primaryEmail: org.primaryEmail ?? "",
    primaryMobile: org.primaryMobile ?? "",
    contacts: normalizeOrgContacts(org.contacts),
    portalRoleId: org.portalRoleId ?? "",
    portalAdmin: { ...emptyPortalAdmin(), ...(typeof org.portalAdmin === "object" ? org.portalAdmin : {}) },
  };
}

export default function OrganizationDetailsTab({
  orgFlat,
  setOrgFlat,
  orgId,
  maxHierarchyLevel,
  onSaved,
  tenantId: tenantIdProp,
  auditActor = "",
  /** When set, create/update calls the API first; must return `{ organizationId }` for creates. */
  persistOrganization,
  /** When set, `(code, excludeOrganizationId?) => Promise<boolean>` — true if code is available. */
  verifyOrganizationCodeAvailable,
}) {
  const existing = useMemo(() => (orgId ? orgFlat.find((o) => o.id === orgId) : null), [orgFlat, orgId]);
  const isNew = !orgId;

  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getUsersRolesList(0, 100);
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [];
        const internal = list.filter((r) => !r?.roleType || String(r.roleType).toUpperCase() === "INTERNAL");
        const source = internal.length ? internal : list;
        const opts = source.map((r) => ({
          value: String(r._id),
          label: `${r.roleID ?? "—"} — ${r.roleDescription ?? ""}`,
        }));
        if (!cancelled) setRoleOptions(opts);
      } catch (err) {
        if (!cancelled) {
          toast.warn(typeof err === "string" ? err : err?.message || "Could not load portal roles.");
          setRoleOptions([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (existing) {
      setForm(formFromExisting(existing));
    } else if (isNew) {
      setForm(initialForm());
    }
    setErrors({});
  }, [existing, isNew, orgId]);

  const parentOptions = useMemo(
    () =>
      orgFlat
        .filter((o) => o.id !== orgId)
        .map((o) => ({ value: o.id, label: `${o.code} — ${o.name}` })),
    [orgFlat, orgId]
  );

  const selectedPortalRole = useMemo(
    () => roleOptions.find((o) => o.value === form.portalRoleId) ?? null,
    [roleOptions, form.portalRoleId]
  );

  const { levels, paths } = useMemo(() => buildTreeWithMeta(orgFlat), [orgFlat]);

  const hierarchyPreview = useMemo(() => {
    if (orgId) {
      return { level: levels[orgId], path: paths[orgId] };
    }
    const probeId = "__new__";
    const probe = {
      id: probeId,
      code: form.code || "(code)",
      name: form.name || "(name)",
      type: form.type,
      parentId: form.parentId,
      status: form.status,
    };
    const flatProbe = [...orgFlat, probe];
    const { levels: lv, paths: pt } = buildTreeWithMeta(flatProbe);
    return { level: lv[probeId], path: pt[probeId] };
  }, [orgId, orgFlat, form.code, form.name, form.type, form.parentId, form.status, levels, paths]);

  const displayTenantId = tenantIdProp ?? existing?.tenantId ?? "";

  const setField = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const setPortalAdminField = (key, value) =>
    setForm((p) => ({
      ...p,
      portalAdmin: { ...emptyPortalAdmin(), ...p.portalAdmin, [key]: value },
    }));

  const addContact = () => setForm((p) => ({ ...p, contacts: [...p.contacts, emptyContact()] }));

  const removeContact = (idx) => setForm((p) => ({ ...p, contacts: p.contacts.filter((_, i) => i !== idx) }));

  const patchContact = (idx, patch) =>
    setForm((p) => ({
      ...p,
      contacts: p.contacts.map((c, i) => (i === idx ? { ...c, ...patch } : c)),
    }));

  const save = async () => {
    const e = {};
    const code = form.code.trim();
    const name = form.name.trim();
    if (!code) e.code = "Organization code is required.";
    else if (orgFlat.some((x) => x.code.trim() === code && x.id !== orgId)) {
      e.code = "Code must be unique within the tenant.";
    }
    if (!name) e.name = "Organization name is required.";

    const pa = form.portalAdmin || {};
    if (persistOrganization && isNew) {
      if (!String(form.portalRoleId || "").trim()) {
        e.portalRoleId = "Portal role is required when saving to the server.";
      }
      if (!String(pa.username || "").trim()) e.portalAdminUsername = "Portal admin username is required.";
      if (!String(pa.password || "").trim()) e.portalAdminPassword = "Portal admin password is required.";
      else if (String(pa.password).length < 8) e.portalAdminPassword = "Password must be at least 8 characters.";
      if (String(pa.password || "").trim() !== String(pa.confirmPassword || "").trim()) {
        e.portalAdminConfirm = "Password and confirm password must match.";
      }
      if (!String(pa.firstName || "").trim()) e.portalAdminFirstName = "First name is required.";
      if (!String(pa.lastName || "").trim()) e.portalAdminLastName = "Last name is required.";
      if (!String(pa.mobile || "").trim()) e.portalAdminMobile = "Mobile is required.";
    }
    if (persistOrganization && !isNew && pa.password?.trim()) {
      if (pa.password.length < 8) e.portalAdminPassword = "Password must be at least 8 characters.";
      if (pa.password !== String(pa.confirmPassword || "").trim()) {
        e.portalAdminConfirm = "Password and confirm password must match.";
      }
    }

    const provisionalId = orgId || `org-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    if (orgId) {
      const v = validateReparent(orgFlat, orgId, form.parentId, maxHierarchyLevel);
      if (!v.ok) e.parentId = v.reason;
    } else if (form.parentId) {
      const probeId = "__new__";
      const flatProbe = [
        ...orgFlat,
        { id: probeId, code, name, type: form.type, parentId: form.parentId, status: form.status },
      ];
      const v = validateReparent(flatProbe, probeId, form.parentId, maxHierarchyLevel);
      if (!v.ok) e.parentId = v.reason;
    }

    if (!e.code && verifyOrganizationCodeAvailable) {
      try {
        const ok = await verifyOrganizationCodeAvailable(code, orgId || undefined);
        if (!ok) e.code = "This organization code is already in use.";
      } catch {
        /* Endpoint missing or network error — do not block save; local uniqueness still applies. */
        toast.warn(
          "Could not verify organization code with the server (check-code API). Save will continue if other checks pass."
        );
      }
    }

    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    const nowIso = new Date().toISOString();
    const actor = auditActor?.trim() || "system";

    const persistSlice = {
      code,
      name,
      shortName: form.shortName.trim(),
      type: form.type,
      parentId: form.parentId,
      status: form.status,
      legalEntity: form.legalEntity,
      operationalEntity: form.operationalEntity,
      effectiveFrom: form.effectiveFrom.trim(),
      effectiveTo: form.effectiveTo.trim(),
      currency: form.currency,
      country: form.country,
      state: form.state.trim(),
      city: form.city.trim(),
      address1: form.address1.trim(),
      address2: form.address2.trim(),
      postalCode: form.postalCode.trim(),
      timeZone: form.timeZone,
      primaryContact: form.primaryContact.trim(),
      primaryEmail: form.primaryEmail.trim(),
      primaryMobile: form.primaryMobile.trim(),
      contacts: form.contacts,
      tenantId: tenantIdProp ?? existing?.tenantId ?? "",
      portalRoleId: String(form.portalRoleId || "").trim(),
      portalAdmin: { ...emptyPortalAdmin(), ...form.portalAdmin },
    };

    const persistSliceForLocal = {
      ...persistSlice,
      portalAdmin: (() => {
        const pa = { ...emptyPortalAdmin(), ...persistSlice.portalAdmin };
        return { ...pa, password: "", confirmPassword: "" };
      })(),
    };

    const applyLocal = (resolvedId) => {
      setOrgFlat((prev) => {
        let next;
        if (orgId) {
          next = prev.map((row) =>
            row.id === orgId
              ? {
                  ...row,
                  ...persistSliceForLocal,
                  updatedBy: actor,
                  updatedOn: nowIso,
                }
              : row
          );
        } else {
          const row = {
            ...ORG_ROW_DEFAULTS,
            ...persistSliceForLocal,
            id: resolvedId,
            customers: [],
            createdBy: actor,
            createdOn: nowIso,
            updatedBy: actor,
            updatedOn: nowIso,
          };
          next = [...prev, row];
        }
        const { levels: lv, paths: pt } = buildTreeWithMeta(next);
        return next.map((r) => ({
          ...r,
          hierarchyLevel: lv[r.id],
          hierarchyPath: pt[r.id],
        }));
      });
      toast.success(orgId ? "Organization updated." : "Organization created.");
      onSaved?.(resolvedId);
    };

    if (persistOrganization) {
      setSaving(true);
      try {
        let mergedRow;
        if (orgId) {
          const base = orgFlat.find((o) => o.id === orgId);
          mergedRow = {
            ...ORG_ROW_DEFAULTS,
            ...base,
            ...persistSlice,
            id: orgId,
            updatedBy: actor,
            updatedOn: nowIso,
          };
          await persistOrganization({ mode: "update", row: mergedRow });
          applyLocal(orgId);
        } else {
          mergedRow = {
            ...ORG_ROW_DEFAULTS,
            ...persistSlice,
            id: provisionalId,
            customers: [],
            createdBy: actor,
            createdOn: nowIso,
            updatedBy: actor,
            updatedOn: nowIso,
          };
          const remote = await persistOrganization({ mode: "create", row: mergedRow });
          const serverId = remote?.organizationId || provisionalId;
          mergedRow = {
            ...mergedRow,
            id: serverId,
            portalAdmin: {
              ...emptyPortalAdmin(),
              ...mergedRow.portalAdmin,
              password: "",
              confirmPassword: "",
            },
          };
          setOrgFlat((prev) => {
            const next = [...prev, mergedRow];
            const { levels: lv, paths: pt } = buildTreeWithMeta(next);
            return next.map((r) => ({
              ...r,
              hierarchyLevel: lv[r.id],
              hierarchyPath: pt[r.id],
            }));
          });
          toast.success("Organization created.");
          onSaved?.(serverId);
        }
      } catch (err) {
        const msg = typeof err === "string" ? err : err?.message || "Save failed.";
        toast.error(String(msg));
      } finally {
        setSaving(false);
      }
      return;
    }

    applyLocal(orgId || provisionalId);
  };

  return (
    <div className="org-details-tab">
      <div className="card border-0 shadow-sm rounded-3 org-panel-card">
        <div className="card-body p-3 p-md-4">
          <h2 className="h6 fw-semibold text-body-secondary text-uppercase small mb-3">Organization details</h2>

          <h3 className="h6 text-body mb-2">Identifiers &amp; hierarchy</h3>
          <div className="row g-3">
            {!isNew && (
              <>
                <div className="col-md-6">
                  <label className="form-label fw-semibold small" htmlFor="org-id-display">
                    Organization ID
                  </label>
                  <input
                    id="org-id-display"
                    className="form-control form-control-sm bg-body-secondary"
                    readOnly
                    value={orgId || ""}
                  />
                  <div className="form-text small">System-generated unique identifier for each node.</div>
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold small" htmlFor="org-tenant-id">
                    Tenant ID
                  </label>
                  <input
                    id="org-tenant-id"
                    className="form-control form-control-sm bg-body-secondary"
                    readOnly
                    value={displayTenantId}
                    placeholder="From signed-in tenant"
                  />
                  <div className="form-text small">Root tenant linkage.</div>
                </div>
              </>
            )}
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-code">
                Organization code <span className="text-danger">*</span>
              </label>
              <input
                id="org-code"
                className={`form-control form-control-sm ${errors.code ? "is-invalid" : ""}`}
                value={form.code}
                onChange={(ev) => setField("code", ev.target.value)}
                autoComplete="off"
              />
              {errors.code && <div className="invalid-feedback d-block">{errors.code}</div>}
              <div className="form-text small">Business code for the organization node.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-name">
                Organization name <span className="text-danger">*</span>
              </label>
              <input
                id="org-name"
                className={`form-control form-control-sm ${errors.name ? "is-invalid" : ""}`}
                value={form.name}
                onChange={(ev) => setField("name", ev.target.value)}
              />
              {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
              <div className="form-text small">Business name of the node.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-short-name">
                Organization short name
              </label>
              <input
                id="org-short-name"
                className="form-control form-control-sm"
                value={form.shortName}
                onChange={(ev) => setField("shortName", ev.target.value)}
              />
              <div className="form-text small">Abbreviated name for UI and reports.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-type">
                Organization type
              </label>
              <select
                id="org-type"
                className="form-select form-select-sm"
                value={form.type}
                onChange={(ev) => setField("type", ev.target.value)}
                disabled={form.type === "Tenant"}
                title={form.type === "Tenant" ? "Tenant is fixed for the system root organization." : undefined}
              >
                {form.type === "Tenant" ? (
                  <option value="Tenant">Tenant (system root)</option>
                ) : (
                  ORG_TYPES_MASTER.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))
                )}
              </select>
              <div className="form-text small">Tenant, company, division, department, BU, or configurable node type.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small">Parent organization</label>
              <Select
                inputId="org-parent"
                isClearable
                options={parentOptions}
                value={parentOptions.find((o) => o.value === form.parentId) ?? null}
                onChange={(opt) => setField("parentId", opt?.value ?? null)}
                classNamePrefix="react-select"
                placeholder="Search parent…"
              />
              {errors.parentId && <div className="text-danger small mt-1">{errors.parentId}</div>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-parent-id-ro">
                Parent organization ID
              </label>
              <input
                id="org-parent-id-ro"
                className="form-control form-control-sm bg-body-secondary"
                readOnly
                value={form.parentId ?? ""}
                placeholder="(root)"
              />
              <div className="form-text small">Parent node reference for the hierarchy.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-h-level">
                Hierarchy level
              </label>
              <input
                id="org-h-level"
                className="form-control form-control-sm bg-body-secondary"
                readOnly
                value={hierarchyPreview.level ?? "—"}
              />
              <div className="form-text small">Numeric hierarchy depth (derived).</div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-h-path">
                Hierarchy path
              </label>
              <input
                id="org-h-path"
                className="form-control form-control-sm bg-body-secondary"
                readOnly
                value={hierarchyPreview.path ?? "—"}
              />
              <div className="form-text small">Full derived ancestry path.</div>
            </div>
          </div>

          <hr className="my-4" />
          <h3 className="h6 text-body mb-2">Portal access</h3>
          <p className="small text-body-secondary mb-3">
            Assign the organization portal admin role (Mongo <code className="small">_id</code> from{" "}
            <code className="small">auth/roles/fetch</code>) and the initial admin user sent as{" "}
            <code className="small">portalRoleId</code> / <code className="small">portalAdmin</code> on create.
            {persistOrganization && isNew ? (
              <span className="d-block mt-1">These fields are required when saving to the server.</span>
            ) : null}
          </p>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-portal-role">
                Portal role
              </label>
              <Select
                inputId="org-portal-role"
                isClearable
                options={roleOptions}
                value={selectedPortalRole}
                onChange={(opt) => setField("portalRoleId", opt?.value ?? "")}
                classNamePrefix="react-select"
                placeholder={roleOptions.length ? "Select role…" : "Loading roles…"}
              />
              {errors.portalRoleId && <div className="text-danger small mt-1">{errors.portalRoleId}</div>}
              <div className="form-text small">Use the role document id (<code className="small">_id</code>), not the role code.</div>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-pa-username">
                Portal admin username {persistOrganization && isNew ? <span className="text-danger">*</span> : null}
              </label>
              <input
                id="org-pa-username"
                type="email"
                autoComplete="off"
                className={`form-control form-control-sm ${errors.portalAdminUsername ? "is-invalid" : ""}`}
                value={form.portalAdmin?.username ?? ""}
                onChange={(ev) => setPortalAdminField("username", ev.target.value)}
              />
              {errors.portalAdminUsername && (
                <div className="invalid-feedback d-block">{errors.portalAdminUsername}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-pa-first">
                Portal admin first name {persistOrganization && isNew ? <span className="text-danger">*</span> : null}
              </label>
              <input
                id="org-pa-first"
                className={`form-control form-control-sm ${errors.portalAdminFirstName ? "is-invalid" : ""}`}
                value={form.portalAdmin?.firstName ?? ""}
                onChange={(ev) => setPortalAdminField("firstName", ev.target.value)}
              />
              {errors.portalAdminFirstName && (
                <div className="invalid-feedback d-block">{errors.portalAdminFirstName}</div>
              )}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-pa-last">
                Portal admin last name {persistOrganization && isNew ? <span className="text-danger">*</span> : null}
              </label>
              <input
                id="org-pa-last"
                className={`form-control form-control-sm ${errors.portalAdminLastName ? "is-invalid" : ""}`}
                value={form.portalAdmin?.lastName ?? ""}
                onChange={(ev) => setPortalAdminField("lastName", ev.target.value)}
              />
              {errors.portalAdminLastName && (
                <div className="invalid-feedback d-block">{errors.portalAdminLastName}</div>
              )}
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold small" htmlFor="org-pa-cc">
                Country code
              </label>
              <select
                id="org-pa-cc"
                className="form-select form-select-sm"
                value={form.portalAdmin?.countryCode ?? "+91"}
                onChange={(ev) => setPortalAdminField("countryCode", ev.target.value)}
              >
                {PHONE_COUNTRY_CODES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label fw-semibold small" htmlFor="org-pa-mobile">
                Mobile {persistOrganization && isNew ? <span className="text-danger">*</span> : null}
              </label>
              <input
                id="org-pa-mobile"
                className={`form-control form-control-sm ${errors.portalAdminMobile ? "is-invalid" : ""}`}
                value={form.portalAdmin?.mobile ?? ""}
                onChange={(ev) => setPortalAdminField("mobile", ev.target.value)}
              />
              {errors.portalAdminMobile && <div className="invalid-feedback d-block">{errors.portalAdminMobile}</div>}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-pa-pw">
                Portal admin password {persistOrganization && isNew ? <span className="text-danger">*</span> : null}
              </label>
              <input
                id="org-pa-pw"
                type="password"
                autoComplete="new-password"
                className={`form-control form-control-sm ${errors.portalAdminPassword ? "is-invalid" : ""}`}
                value={form.portalAdmin?.password ?? ""}
                onChange={(ev) => setPortalAdminField("password", ev.target.value)}
              />
              {errors.portalAdminPassword && (
                <div className="invalid-feedback d-block">{errors.portalAdminPassword}</div>
              )}
              {!isNew ? <div className="form-text small">Leave blank to keep the current password.</div> : null}
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-pa-pw2">
                Confirm password {persistOrganization && isNew ? <span className="text-danger">*</span> : null}
              </label>
              <input
                id="org-pa-pw2"
                type="password"
                autoComplete="new-password"
                className={`form-control form-control-sm ${errors.portalAdminConfirm ? "is-invalid" : ""}`}
                value={form.portalAdmin?.confirmPassword ?? ""}
                onChange={(ev) => setPortalAdminField("confirmPassword", ev.target.value)}
              />
              {errors.portalAdminConfirm && <div className="invalid-feedback d-block">{errors.portalAdminConfirm}</div>}
            </div>
          </div>

          <hr className="my-4" />
          <h3 className="h6 text-body mb-2">Entity flags, geography &amp; currency</h3>
          <div className="row g-3">
            <div className="col-md-6 col-lg-4">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="org-legal"
                  checked={form.legalEntity}
                  onChange={(ev) => setField("legalEntity", ev.target.checked)}
                />
                <label className="form-check-label small" htmlFor="org-legal">
                  Legal entity
                </label>
              </div>
              <div className="form-text small">Whether the node is a legal/commercial entity.</div>
            </div>
            <div className="col-md-6 col-lg-4">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="org-operational"
                  checked={form.operationalEntity}
                  onChange={(ev) => setField("operationalEntity", ev.target.checked)}
                />
                <label className="form-check-label small" htmlFor="org-operational">
                  Operational entity
                </label>
              </div>
              <div className="form-text small">Whether the node can transact operationally.</div>
            </div>
            <div className="col-md-6 col-lg-4">
              <label className="form-label fw-semibold small" htmlFor="org-currency">
                Default currency
              </label>
              <select
                id="org-currency"
                className="form-select form-select-sm"
                value={form.currency}
                onChange={(ev) => setField("currency", ev.target.value)}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-country">
                Default country
              </label>
              <select
                id="org-country"
                className="form-select form-select-sm"
                value={form.country}
                onChange={(ev) => setField("country", ev.target.value)}
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-tz">
                Time zone
              </label>
              <select
                id="org-tz"
                className="form-select form-select-sm"
                value={form.timeZone}
                onChange={(ev) => setField("timeZone", ev.target.value)}
              >
                {TIME_ZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <hr className="my-4" />
          <h3 className="h6 text-body mb-2">Primary &amp; additional contacts</h3>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-pc-name">
                Primary contact name
              </label>
              <input
                id="org-pc-name"
                className="form-control form-control-sm"
                value={form.primaryContact}
                onChange={(ev) => setField("primaryContact", ev.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-pc-email">
                Primary contact email
              </label>
              <input
                id="org-pc-email"
                type="email"
                className="form-control form-control-sm"
                value={form.primaryEmail}
                onChange={(ev) => setField("primaryEmail", ev.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-pc-mobile">
                Primary contact mobile
              </label>
              <input
                id="org-pc-mobile"
                className="form-control form-control-sm"
                value={form.primaryMobile}
                onChange={(ev) => setField("primaryMobile", ev.target.value)}
              />
            </div>
          </div>

          <div className="mt-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <span className="fw-semibold small">Additional contacts</span>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={addContact}>
                <i className="bi bi-plus-lg me-1" aria-hidden />
                Add contact
              </button>
            </div>
            {form.contacts.length === 0 ? (
              <p className="text-muted small mb-0">No additional contacts. Use Add contact for repeating contact lines.</p>
            ) : (
              <div className="table-responsive rounded border">
                <table className="table table-sm table-borderless mb-0 align-middle">
                  <thead className="table-light">
                    <tr className="small text-muted">
                      <th className="ps-3">Name</th>
                      <th>Email</th>
                      <th>Mobile</th>
                      <th>Role</th>
                      <th className="text-end pe-3" style={{ width: "4rem" }}>
                        {" "}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.contacts.map((c, idx) => (
                      <tr key={c.id} className="border-top">
                        <td className="ps-3">
                          <input
                            className="form-control form-control-sm"
                            value={c.name}
                            onChange={(ev) => patchContact(idx, { name: ev.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            type="email"
                            value={c.email}
                            onChange={(ev) => patchContact(idx, { email: ev.target.value })}
                          />
                        </td>
                        <td>
                          <input
                            className="form-control form-control-sm"
                            value={c.mobile}
                            onChange={(ev) => patchContact(idx, { mobile: ev.target.value })}
                          />
                        </td>
                        <td>
                          <select
                            className="form-select form-select-sm"
                            value={c.role}
                            onChange={(ev) => patchContact(idx, { role: ev.target.value })}
                          >
                            {CONTACT_ROLES.map((r) => (
                              <option key={r.value || "none"} value={r.value}>
                                {r.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-end pe-3">
                          <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0"
                            onClick={() => removeContact(idx)}
                            aria-label="Remove contact"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <hr className="my-4" />
          <h3 className="h6 text-body mb-2">Address</h3>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-addr1">
                Address line 1
              </label>
              <input
                id="org-addr1"
                className="form-control form-control-sm"
                value={form.address1}
                onChange={(ev) => setField("address1", ev.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold small" htmlFor="org-addr2">
                Address line 2
              </label>
              <input
                id="org-addr2"
                className="form-control form-control-sm"
                value={form.address2}
                onChange={(ev) => setField("address2", ev.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-city">
                City
              </label>
              <input
                id="org-city"
                className="form-control form-control-sm"
                value={form.city}
                onChange={(ev) => setField("city", ev.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-state">
                State / region
              </label>
              <input
                id="org-state"
                className="form-control form-control-sm"
                value={form.state}
                onChange={(ev) => setField("state", ev.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold small">Country (address)</label>
              <input
                className="form-control form-control-sm bg-body-secondary"
                readOnly
                value={COUNTRIES.find((c) => c.code === form.country)?.label ?? form.country}
              />
              <div className="form-text small">Same as default country.</div>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-postal">
                Postal code
              </label>
              <input
                id="org-postal"
                className="form-control form-control-sm"
                value={form.postalCode}
                onChange={(ev) => setField("postalCode", ev.target.value)}
              />
            </div>
          </div>

          <hr className="my-4" />
          <h3 className="h6 text-body mb-2">Lifecycle &amp; status</h3>
          <div className="row g-3">
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-status">
                Status
              </label>
              <select
                id="org-status"
                className="form-select form-select-sm"
                value={form.status}
                onChange={(ev) => setField("status", ev.target.value)}
              >
                {ORG_STATUS.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
              <div className="form-text small">Active, inactive, blocked, or archived.</div>
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-eff-from">
                Effective from
              </label>
              <input
                id="org-eff-from"
                type="date"
                className="form-control form-control-sm"
                value={form.effectiveFrom}
                onChange={(ev) => setField("effectiveFrom", ev.target.value)}
              />
            </div>
            <div className="col-md-4">
              <label className="form-label fw-semibold small" htmlFor="org-eff-to">
                Effective to
              </label>
              <input
                id="org-eff-to"
                type="date"
                className="form-control form-control-sm"
                value={form.effectiveTo}
                onChange={(ev) => setField("effectiveTo", ev.target.value)}
              />
              <div className="form-text small">End date if deactivated.</div>
            </div>
          </div>

          {!isNew && existing && (
            <>
              <hr className="my-4" />
              <h3 className="h6 text-body mb-2">Audit</h3>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold small">Created by</label>
                  <input className="form-control form-control-sm bg-body-secondary" readOnly value={existing.createdBy ?? "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold small">Created on</label>
                  <input className="form-control form-control-sm bg-body-secondary" readOnly value={existing.createdOn ?? "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold small">Updated by</label>
                  <input className="form-control form-control-sm bg-body-secondary" readOnly value={existing.updatedBy ?? "—"} />
                </div>
                <div className="col-md-6">
                  <label className="form-label fw-semibold small">Updated on</label>
                  <input className="form-control form-control-sm bg-body-secondary" readOnly value={existing.updatedOn ?? "—"} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="org-sticky-actions rounded-3 mt-3">
        <div className="d-flex flex-wrap gap-2 justify-content-end">
          <button type="button" className="btn btn-sm btn-primary px-3" onClick={save} disabled={saving}>
            <i className="bi bi-check2-circle me-2" aria-hidden />
            {saving ? "Saving…" : "Save organization"}
          </button>
        </div>
      </div>
    </div>
  );
}
